import React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import first from 'lodash/first';
import takeRightWhile from 'lodash/takeRightWhile';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import Asset, { type TinaAsset } from 'components/TinaAsset';
import type { Maybe } from '.tina/__generated__/types';
import { breakpointKeys, type ResponsiveImageBreakpoints } from 'lib/tina/responsiveAsset';

interface Wrapper {
  $horizontalPadding?: boolean;
  max?: ResponsiveImageBreakpoints;
  min?: ResponsiveImageBreakpoints;
  $maxHeightMobile?: boolean;
}

interface IBreakpointImage {
  $borderRadius?: number;
}

const Wrapper = styled.div<Wrapper>`
  padding: ${({ $horizontalPadding }) => ($horizontalPadding ? '0 1.5rem' : '0')};
  display: flex;
  flex: 1;
  ${({ theme, $maxHeightMobile }) =>
    $maxHeightMobile &&
    theme.mq.ltLg`
    max-height: 100vh;
    flex: 0;
    margin: 0 auto;
  `}
  ${({ max, theme }) =>
    max &&
    theme.mq[max]`
      display: none;
    `}

  ${({ min, theme }) =>
    min &&
    min !== 'xs' &&
    theme.mq[`lt${capitalize(min)}` as keyof DefaultTheme['mq']]`
      display: none;
    `}
`;

const BreakpointImage = styled(Asset)<TinaAsset & IBreakpointImage>`
  border-radius: ${({ $borderRadius }) => $borderRadius && `${$borderRadius}px`};
  padding: 0;
`;

function getNextBreakpoint(
  currentBp: ResponsiveImageBreakpoints,
  props: TinaResponsiveAsset['asset']
): ResponsiveImageBreakpoints | undefined {
  const definedKeys = breakpointKeys.filter(k => props?.[k]);
  return first(
    takeRightWhile(compact(definedKeys), bp => bp !== currentBp)
  ) as ResponsiveImageBreakpoints;
}

interface TinaResponsiveAsset {
  asset?: Maybe<{
    [p in ResponsiveImageBreakpoints]?: TinaAsset['asset'];
  }>;
  className?: string;
}

const TinaResponsiveAsset: React.FC<TinaResponsiveAsset> = ({ asset, className }) => {
  if (isEmpty(asset)) {
    return null;
  }
  return (
    <>
      {breakpointKeys
        .filter(breakpoint => asset?.[breakpoint])
        .map(breakpoint => {
          const max = getNextBreakpoint(breakpoint, asset);

          const { borderRadius, maxHeightMobile, horizontalPadding, ...bpAsset } = (asset?.[
            breakpoint
          ] || {}) as {
            borderRadius: number;
            maxHeightMobile: boolean;
            horizontalPadding: boolean;
          };

          return (
            <Wrapper
              key={breakpoint}
              $horizontalPadding={!!horizontalPadding}
              min={breakpoint}
              max={max}
              $maxHeightMobile={maxHeightMobile || undefined}
            >
              <BreakpointImage
                asset={asset?.[breakpoint]}
                className={className}
                $borderRadius={borderRadius}
                {...bpAsset}
              />
            </Wrapper>
          );
        })}
    </>
  );
};

export default styled(TinaResponsiveAsset)``;
